import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import type { QuestionFormData, QuestionNodeData } from '@app/services/aiFlow.types';
import { StyledButtonGroup } from '@app/components/button-group/ButtonGroup';
import { StyledButton } from '@app/components/button/Button';

type QuestionFormProps = {
    readonly id: string;
    readonly data: QuestionNodeData;
    readonly number: string | null;
    readonly onSubmit: (data: QuestionNodeData) => void;
    readonly onCancel: () => void;
};

const schema = z.object({
    text: z.string().trim().min(1, 'Question is required'),
    options: z
        .string()
        .trim()
        .refine(
            (val) => {
                if (val.trim() === '') return true;
                const optsArr = val
                    .split(',')
                    .map((opt) => opt.trim().toLowerCase())
                    .filter((opt) => opt.length > 0);
                if (optsArr.length < 2) return false;
                return new Set(optsArr).size === optsArr.length;
            },
            { message: 'If required, provide at least 2 unique Options' }
        ),
    skippable: z.boolean(),
    spelling_sensitive: z.boolean(),
    required: z.boolean(),
    visConditionSlots: z.coerce.number().min(0).max(3),
});

function QuestionForm({ id, data, number, onSubmit, onCancel }: QuestionFormProps) {
    const {
        register,
        handleSubmit,
        reset,
        formState: { isValid, isDirty },
    } = useForm({
        resolver: zodResolver(schema),
        defaultValues: {
            ...data,
            options: data.options ? data.options.join(', ') : '',
        },
    });

    useEffect(() => {
        reset({
            ...data,
            options: data.options ? data.options.join(', ') : '',
        });
    }, [data, reset]);

    const handleOnSubmit = (formData: QuestionFormData) => {
        const optionsArr = formData.options.split(',').reduce<string[]>((acc, word) => {
            const option = word.trim();
            if (option.length > 0) {
                acc.push(option);
            }
            return acc;
        }, []);
        onSubmit({
            ...formData,
            options: optionsArr,
        });
    };

    return (
        <>
            <div className="order-tag">
                <span>{number ?? '--'}</span>
            </div>
            <form onSubmit={handleSubmit(handleOnSubmit)} key={id}>
                <label htmlFor="text-input">
                    Question
                    <textarea {...register('text')} id="text-input" autoFocus />
                </label>
                <label htmlFor="options-input">
                    Options <span className="label-note">(comma separated)</span>
                    <input {...register('options')} id="options-input" autoComplete="off" />
                    <div style={{ fontSize: '13px', textAlign: 'right' }}>
                        If required, 2 or more unique values
                    </div>
                </label>
                <div className="grid">
                    <label htmlFor="required-input" className="inline-label">
                        Required
                        <input {...register('required')} id="required-input" type="checkbox" />
                    </label>
                    <label htmlFor="spelling_sensitive-input" className="inline-label">
                        Spelling Sensitive
                        <input
                            {...register('spelling_sensitive')}
                            id="spelling_sensitive-input"
                            type="checkbox"
                        />
                    </label>
                    <label htmlFor="skippable-input" className="inline-label">
                        Skippable
                        <input {...register('skippable')} id="skippable-input" type="checkbox" />
                    </label>
                    <label htmlFor="visConditionSlots-input" className="inline-label">
                        <span>Condition slots</span>
                        <input
                            {...register('visConditionSlots')}
                            id="visConditionSlots-input"
                            type="number"
                            max={3}
                            min={0}
                        />
                    </label>
                </div>
                <StyledButtonGroup className="margin-top-10">
                    <StyledButton htmlType="submit" disabled={!isValid || !isDirty}>
                        Save
                    </StyledButton>
                    <StyledButton htmlType="button" onClick={onCancel}>
                        Cancel
                    </StyledButton>
                </StyledButtonGroup>
            </form>
        </>
    );
}

export default QuestionForm;
